/**
 * 跨天未签退对账 hook（规则2 检测源，对齐 mobile reconcileService.fetchOpenAttendance）。
 * 首次加载 + 公共刷新信号查询 POST /pms/attendance/search，找任意日期未闭合考勤。
 * 跨日、窗口恢复、资源通知和五分钟校验共用同一刷新入口。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshSignal } from "@/features/realtime/useRefreshSignal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { attendanceApi } from "@/api/api";
import { workSessions } from "@/services/workSessionService";
import { serverNowMs } from "@/utils/serverClock";
import type { SnowflakeId } from "@/types/snowflake";
import type { CrossDayRecord } from "./crossDay";

import { todayKey } from '@/utils/businessDay'
dayjs.extend(utc);
dayjs.extend(timezone);

/** 今天业务日 YYYY-MM-DD（server 校准时间 + 用户时区）。 */
export function todayBizDate(userTz: string): string {
  return todayKey(userTz, new Date(serverNowMs()));
}

interface SearchRow {
  id: string;
  attendanceDate: string;
  attendanceStatus: string;
  firstClockInAt?: string | null;
  lastBreakStartAt?: string | null;
  lastClockInAt?: string | null;
  lastBreakEndAt?: string | null;
}

/** max(非空 ISO) → ISO|null。 */
function maxIso(...xs: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  for (const x of xs) {
    if (!x) continue;
    if (best == null || dayjs(x).valueOf() > dayjs(best).valueOf()) best = x;
  }
  return best;
}

/** 从当天 task work session 行里算 max(endedAt ?? serverNow) → ISO|null。 */
export function pickLastTaskSessionEndedAt(
  rows: Array<{ endedAt?: string | null }>,
  nowMs: number,
): string | null {
  let bestMs = Number.NEGATIVE_INFINITY;
  let best: string | null = null;
  for (const r of rows) {
    // open session(endedAt 为 null)：工作实际持续到 now，用 serverNow 参与 max
    // ——与 crossDay.computeDefaultClockOut 里 openSpecialNowMs 的语义一致。
    const ms = r.endedAt ? dayjs(r.endedAt).valueOf() : nowMs;
    if (Number.isNaN(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = r.endedAt ?? new Date(nowMs).toISOString();
    }
  }
  return best;
}

/**
 * 该考勤日内 task work session 的"最后干活时刻"= max(endedAt ?? serverNow)。
 *
 * 与 `useOpenSessions` 的口径正交：那边要的是**未结束**(endedAt IS NULL)的 open
 * session 用来判 detected=activity；这里要的是"最后一次真正在干活"的时刻，用来
 * 把补签退默认时刻顶上去（break_end 之后继续干活到半夜忘打卡的场景，attendance
 * 聚合完全看不到）。
 *
 * ⚠️ 这里**不能**再按 endedAt is_not_null 过滤：mobile 起的 **regular** task
 * session 跨天时 endedAt 仍为 null，过滤掉后一条都查不到，默认补签退时刻退回
 * clock_in、整段班工时清零（Candice 案例；`computeDefaultClockOut` 的
 * openSpecialNowMs 只兜住 special，regular 在 web 侧仍缺口）。改为拉当天全部
 * session，在客户端把 open 的那条按 serverNow 计入 max。
 *
 * 时间窗按 startedAt 落在该业务日（跟 useDailyWorkDetail / useUserDayActivity
 * 同口径）；跨零点结束的 session 其 endedAt 会晚于日末刻，但 computeResolveTimes
 * 会统一夹到日末，不会写到次日那行。
 *
 * 查询失败不阻断对账 —— 返回 null 退化为原有 attendance-only 行为。
 */
async function fetchLastTaskSessionEndedAt(
  userId: SnowflakeId,
  attendanceDate: string,
  userTz: string,
): Promise<string | null> {
  const dayStart = dayjs.tz(attendanceDate, userTz).startOf("day").utc().toISOString();
  const dayEnd = dayjs.tz(attendanceDate, userTz).endOf("day").utc().toISOString();
  try {
    const resp = await workSessions.search({
      filters: {
        // SnowflakeID 必须 type:'string'(精度)
        assigneeId: { type: "string", operator: "equals", value: userId },
        startedAt: { type: "date", operator: "between", value: [dayStart, dayEnd] },
      },
      columns: "id endedAt",
      orderBy: "-startedAt",
      page: 1,
      // ⚠️ 下面 pickLastTaskSessionEndedAt 是**客户端**取 max(endedAt),而这里
      // 排序键是 startedAt —— 两者不同序,被截断的尾部可能正好是最晚结束的那条
      // (最早开始、跨度最长的 session)。所以页容量必须大到覆盖单人单日全部
      // session,不能靠排序兜底。高频短工单一天几十条,500 留足冗余。
      pageSize: 500,
    });
    const rows =
      (resp as unknown as { results?: Array<{ endedAt?: string | null }> })?.results ?? [];
    return pickLastTaskSessionEndedAt(rows, serverNowMs());
  } catch (e) {
    console.error("[useCrossDayCheck] last task session lookup failed:", e);
    return null;
  }
}

async function fetchOpenPriorDayAttendance(
  userId: SnowflakeId,
  bizDate: string,
  userTz: string,
): Promise<CrossDayRecord | null> {
  const agg = (alias: string, sessionType?: string) => ({
    field: "sessions.action_time",
    operation: "max",
    alias,
    ...(sessionType
      ? { filter: { session_type: { type: "string", operator: "equals", value: sessionType } } }
      : {}),
  });
  const resp = (await attendanceApi.searchAttendance({
    filters: {
      userId: { type: "string", operator: "equals", value: userId },
      attendanceStatus: { type: "string", operator: "in", value: ["working", "in_break"] },
      attendanceDate: { type: "date", operator: "lt", value: bizDate },
    },
    aggregates: [
      agg("lastBreakStartAt", "break_start"),
      agg("lastClockInAt", "clock_in"),
      agg("lastBreakEndAt", "break_end"),
    ],
    orderBy: "-attendanceDate",
    page: 1,
    pageSize: 5,
    columns: "id attendanceDate attendanceStatus firstClockInAt",
  })) as unknown as { results?: SearchRow[] };

  const row = resp?.results?.[0];
  if (!row) return null;
  // defensive：search filter 已约束 status，这里防后端 schema 漂移
  if (row.attendanceStatus !== "working" && row.attendanceStatus !== "in_break") {
    return null;
  }
  const lastTaskSessionEndedAt = await fetchLastTaskSessionEndedAt(
    userId,
    row.attendanceDate,
    userTz,
  );
  return {
    attendanceId: String(row.id),
    attendanceDate: row.attendanceDate,
    status: row.attendanceStatus as "working" | "in_break",
    firstClockInAt: row.firstClockInAt ?? null,
    lastBreakStartAt: row.lastBreakStartAt ?? null,
    lastClockInAt: row.lastClockInAt ?? null,
    latestSessionActionTime: maxIso(
      row.lastBreakStartAt,
      row.lastClockInAt,
      row.lastBreakEndAt,
    ),
    lastTaskSessionEndedAt,
  };
}

export function useCrossDayCheck(userId: SnowflakeId | null, userTz: string) {
  const [record, setRecord] = useState<CrossDayRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!userId) {
      setRecord(null);
      return;
    }
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const r = await fetchOpenPriorDayAttendance(
        userId,
        todayBizDate(userTz),
        userTz,
      );
      if (seq !== seqRef.current) return;
      setRecord(r);
    } catch (e) {
      console.error("[useCrossDayCheck] reconcile failed:", e);
      if (seq === seqRef.current) setRecord(null);
      return false;
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [userId, userTz]);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Invalidate all outstanding reads; this ref is a sequence counter.
    return () => { seqRef.current++; };
  }, [refresh]);

  useRefreshSignal({ resource: "myAttendance", enabled: !!userId, onRefresh: refresh });
  return { record, loading, refresh };
}
